import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import Select from 'react-select'
import { Card, CardBody, Col, Container, Form, FormFeedback, Label, Row } from 'reactstrap'
import * as Yup from 'yup'
import { formatIntermediaryDiaryUpdatePayload } from '../../../../api/diaries/intermediaryDiary/formatters'
import intermediaryDiary from '../../../../api/diaries/intermediaryDiary/hooks'
import { IntermediaryDiary } from '../../../../api/diaries/intermediaryDiary/types'
import { RouteParams } from '../../../../api/globalTypes'
import properties from '../../../../api/properties/hooks'
import { APPLICATION_SETTINGS } from '../../../../common/constants'
import { diaryResponseFields } from '../../../../common/response/diary'
import FormActionButtons from '../../../../Components/Common/FormActionButtons'
import Loader from '../../../../Components/Common/Loader'
import PageTitle from '../../../../Components/Common/PageTitle'
import InputDate from '../../../../Components/FormUI/InputDate'
import InputNumber from '../../../../Components/FormUI/InputNumber'
import InputText from '../../../../Components/FormUI/InputText'
import { useFormValidationErrorModal } from '../../../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import { denormalizeDecimalFields, normalizeDecimalFields } from '../../../../utils/general'
import { renderAreaUnit, renderCurrency } from '../../../../utils/property'
import { getDiaryValidationSchema } from './formValidation'
import { inputsConfig } from './inputs'
import style from './IntermediaryDiaryEdit.module.css'

const DECIMAL_FIELDS = [
    diaryResponseFields.AREA,
    diaryResponseFields.PRICE,
    diaryResponseFields.PROPERTY_ADVANCE_AMOUNT,
    diaryResponseFields.COMMISSION_SELLER,
    diaryResponseFields.COMMISSION_BUYER,
]

const commissionTypeOptions = [
    { value: '%', label: '%' },
    { value: '€', label: '€' },
]

const IntermediaryDiaryEdit = () => {
    const { id } = useParams<RouteParams>()
    const history = useHistory()
    const { t } = useTranslation()

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const { data: propertyValues } = properties.FormValues.useQuery(['propertyService'])
    const { data: diary, isPending } = intermediaryDiary.GetDiary.useQuery(id)
    const { mutate: updateDiary } = intermediaryDiary.UpdateDiary.useMutation()

    const formik = useFormik<IntermediaryDiary>({
        enableReinitialize: true,
        initialValues: (diary
            ? {
                  ...diary.item,
                  ...normalizeDecimalFields(diary.item, DECIMAL_FIELDS),
              }
            : {}) as IntermediaryDiary,
        validationSchema: Yup.object(getDiaryValidationSchema()),
        onSubmit: (values) => {
            const denormalizedDiaryFields = denormalizeDecimalFields(values, DECIMAL_FIELDS)

            const payload = {
                ...formatIntermediaryDiaryUpdatePayload(values),
                ...denormalizedDiaryFields,
            }

            updateDiary(payload)
        },
    })

    useFormValidationErrorModal(formik, [inputsConfig()])

    const handleDateChange = (e: Date, propertyName: string) => {
        formik?.setFieldValue(propertyName, dayjs(e).format('YYYY-MM-DD'))
    }

    const handleDropDownChange = (e: unknown, propertyName: string) => {
        formik?.setFieldValue(propertyName, e)
    }

    if (isPending) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loader />
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Form onSubmit={(e) => formik.handleSubmit(e)}>
                    <div className="page-title-box d-flex align-items-center justify-content-between">
                        <PageTitle title={t('app.common.intermediaryDiary')} />
                        <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push('/intermediary-diary')} />
                    </div>
                    <div className="d-flex justify-content-between gap-3">
                        <Col>
                            <Card>
                                <CardBody className={style.cardBody}>
                                    <Label>{t('form.diary.propertyService')}</Label>
                                    <Select
                                        id={formik.values?.propertyService}
                                        onChange={(e) => handleDropDownChange(e?.label, 'propertyService')}
                                        options={propertyValues?.propertyService}
                                        defaultValue={propertyValues?.propertyService?.find(
                                            (x) => x.label.toLowerCase() === diary?.item.propertyService.toLowerCase()
                                        )}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={9}>
                            <Card>
                                <CardBody className={style.cardBody}>
                                    {formik.values?.intermediaryDiaryBuyer?.length > 0 && (
                                        <Label className={style.formLabel}>{t('form.diary.buyerInfo')}</Label>
                                    )}
                                    {formik.values?.intermediaryDiaryBuyer?.map((_, index) => (
                                        <div key={index} className={style.formContent}>
                                            <InputText
                                                label={t('form.diary.fullName')}
                                                className={style.formInput}
                                                id={`intermediaryDiaryBuyer[${index}].fullName`}
                                                formik={formik}
                                                isRequired={true}
                                            />
                                            <InputNumber
                                                maxLength={14}
                                                label={t('app.common.pin')}
                                                className={style.formInput}
                                                id={`intermediaryDiaryBuyer[${index}].taxNumber`}
                                                formik={formik}
                                            />
                                            <InputText
                                                label={t('app.common.city')}
                                                className={style.formInput}
                                                id={`intermediaryDiaryBuyer[${index}].city`}
                                                formik={formik}
                                            />
                                            <InputText
                                                label={t('app.common.street')}
                                                className={style.formInput}
                                                id={`intermediaryDiaryBuyer[${index}].street`}
                                                formik={formik}
                                            />
                                            <InputNumber
                                                label={t('app.common.streetNumber')}
                                                className={style.formInput}
                                                id={`intermediaryDiaryBuyer[${index}].streetNumber`}
                                                formik={formik}
                                            />
                                        </div>
                                    ))}
                                    {formik.values?.intermediaryDiarySeller?.length > 0 && (
                                        <Label className={style.formLabel}>{t('form.diary.sellerInfo')}</Label>
                                    )}
                                    {formik.values?.intermediaryDiarySeller?.map((_, index) => (
                                        <div key={index} className={style.formContent}>
                                            <InputText
                                                label={t('form.diary.fullName')}
                                                className={style.formInput}
                                                id={`intermediaryDiarySeller[${index}].fullName`}
                                                formik={formik}
                                                isRequired={true}
                                            />
                                            <InputNumber
                                                maxLength={14}
                                                label={t('app.common.pin')}
                                                className={style.formInput}
                                                id={`intermediaryDiarySeller[${index}].taxNumber`}
                                                formik={formik}
                                            />
                                            <InputText
                                                label={t('app.common.city')}
                                                className={style.formInput}
                                                id={`intermediaryDiarySeller[${index}].city`}
                                                formik={formik}
                                            />
                                            <InputText
                                                label={t('app.common.street')}
                                                className={style.formInput}
                                                id={`intermediaryDiarySeller[${index}].street`}
                                                formik={formik}
                                            />
                                            <InputNumber
                                                label={t('app.common.streetNumber')}
                                                className={style.formInput}
                                                id={`intermediaryDiarySeller[${index}].streetNumber`}
                                                formik={formik}
                                            />
                                        </div>
                                    ))}
                                    <Label className={style.formLabel}>{t('form.diary.propertyInfo')}</Label>
                                    {formik.submitCount > 0 &&
                                        !formik.values.propertyRegistryParcelNo?.trim() &&
                                        !formik.values.propertyCadastralParcelNo?.trim() && (
                                            <Row>
                                                <FormFeedback type="invalid" className="d-block text-center my-2">
                                                    {t('form.errors.common.parcelNumberRequired')}
                                                </FormFeedback>
                                            </Row>
                                        )}
                                    <div className={style.formContent}>
                                        <InputText
                                            label={t('app.common.property.type')}
                                            className={style.formInput}
                                            id={'propertyType'}
                                            formik={formik}
                                            isRequired={true}
                                        />
                                        <InputNumber
                                            label={`${t('app.common.spaceArea')} (${renderAreaUnit(areaUnit)})`}
                                            className={style.formInput}
                                            id={'areaNett'}
                                            formik={formik}
                                            isRequired={true}
                                            isFormated={true}
                                        />
                                        <InputNumber
                                            label={t('app.common.floor')}
                                            className={style.formInput}
                                            id={'propertyFloorNo'}
                                            formik={formik}
                                        />
                                        <InputNumber
                                            label={t('app.common.rooms')}
                                            className={style.formInput}
                                            id={'propertyRoomsNo'}
                                            formik={formik}
                                        />
                                        <InputText
                                            label={t('app.common.registry.parcel')}
                                            className={style.formInput}
                                            id={'propertyRegistryParcelNo'}
                                            formik={formik}
                                        />
                                        <InputText
                                            label={t('app.common.registry.insert')}
                                            className={style.formInput}
                                            id={'propertyRegistryInsert'}
                                            formik={formik}
                                            isRequired={true}
                                        />
                                        <InputText
                                            label={t('app.common.registry.subInsert')}
                                            className={style.formInput}
                                            id={'propertyRegistrySubinsert'}
                                            formik={formik}
                                        />
                                        <InputText
                                            label={t('app.common.cadastralMunicipality')}
                                            className={style.formInput}
                                            id={'propertyCadastralMunicipality'}
                                            formik={formik}
                                            isRequired={true}
                                        />
                                        <InputText
                                            label={t('app.common.property.cadastralParcel')}
                                            className={style.formInput}
                                            id={'propertyCadastralParcelNo'}
                                            // error suppressed here — the general banner above already explains this field's requirement
                                            formik={{ ...formik, errors: { ...formik.errors, propertyCadastralParcelNo: undefined } }}
                                        />
                                        <InputText
                                            label={t('app.common.energyClass')}
                                            className={style.formInput}
                                            id={'propertyEnergyClass'}
                                            formik={formik}
                                        />
                                    </div>
                                    <Label className={style.formLabel}>{t('form.diary.transactionInfo')}</Label>
                                    <Row className="mb-4 gy-3 w-75">
                                        <Col md={6}>
                                            <InputNumber
                                                label={`${t('form.diary.finalPrice')} ${renderCurrency(currency)}`}
                                                id={'finalPrice'}
                                                formik={formik}
                                                isRequired={true}
                                                isFormated={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputNumber
                                                label={`${t('form.diary.propertyAdvanceAmount')} ${renderCurrency(currency)}`}
                                                id={'propertyAdvanceAmount'}
                                                formik={formik}
                                                isFormated={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputNumber
                                                label={t('app.common.commissionSeller')}
                                                id={'commissionSeller'}
                                                formik={formik}
                                                isFormated={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>{t('app.common.commissionType')}</Label>
                                            <Select
                                                options={commissionTypeOptions}
                                                value={commissionTypeOptions.find((o) => o.value === formik.values?.commissionSellerType) ?? null}
                                                onChange={(e) => handleDropDownChange(e?.value ?? null, 'commissionSellerType')}
                                                isClearable
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputNumber
                                                label={t('app.common.commissionBuyer')}
                                                id={'commissionBuyer'}
                                                formik={formik}
                                                isFormated={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>{t('app.common.commissionType')}</Label>
                                            <Select
                                                options={commissionTypeOptions}
                                                value={commissionTypeOptions.find((o) => o.value === formik.values?.commissionBuyerType) ?? null}
                                                onChange={(e) => handleDropDownChange(e?.value ?? null, 'commissionBuyerType')}
                                                isClearable
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputText
                                                label={t('app.common.conclusionPlace')}
                                                id={'conclusionPlace'}
                                                formik={formik}
                                                isRequired={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputDate
                                                label={t('app.common.conclusionDate')}
                                                id={'conclusionDate'}
                                                onChange={(e: Date) => handleDateChange(e, 'conclusionDate')}
                                                onBlur={formik.handleBlur}
                                                defaultValue={formik.values?.conclusionDate}
                                                formikError={formik.errors}
                                                formikTouched={formik.touched}
                                                isRequired={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputDate
                                                label={t('form.diary.finalPaymentDate')}
                                                id={'finalPaymentDate'}
                                                onChange={(e: Date) => handleDateChange(e, 'finalPaymentDate')}
                                                onBlur={formik.handleBlur}
                                                defaultValue={formik.values?.finalPaymentDate}
                                                formikError={formik.errors}
                                                formikTouched={formik.touched}
                                                isRequired={true}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <InputDate
                                                label={t('app.common.creationDate')}
                                                id={'intermediaryDiaryDate'}
                                                onChange={(e: Date) => handleDateChange(e, 'intermediaryDiaryDate')}
                                                onBlur={formik.handleBlur}
                                                defaultValue={formik.values?.intermediaryDiaryDate}
                                                formikError={formik.errors}
                                                formikTouched={formik.touched}
                                                isRequired={true}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </div>
                    <div className="d-flex justify-content-end mb-4">
                        <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push('/intermediary-diary')} />
                    </div>
                </Form>
            </Container>
        </div>
    )
}

export default IntermediaryDiaryEdit
